import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Typography,
    Box,
    Button,
    CircularProgress,
    Alert,
    Pagination,
} from '@mui/material';
import {
    ArrowBack,
} from '@mui/icons-material';
import { userRequest } from '../../requestMethods';
import VideoPostCard from './VideoPostCard';

const Shorts = () => {
    const navigate = useNavigate();
    const [content, setContent] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);

    // Mock topics for now
    const mockTopics = ['Technology', 'Science', 'History', 'Literature', 'Business', 'Health'];

    const [selectedTopics, setSelectedTopics] = useState([]);

    useEffect(() => {
        const fetchShorts = async () => {
            setLoading(true);
            setError(null);
            
            try {
                const response = await userRequest.get('/client-playlists/watch-shorts', {
                    params: { page, limit: 12 }
                });
                
                setContent(response.data);
            } catch (error) {
                console.error('Error fetching shorts:', error);
                setError(error.response?.data?.error || 'Failed to load shorts');
            } finally {
                setLoading(false);
            }
        };
        
        fetchShorts();
    }, [page]);

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleTopicToggle = (topic) => {
        setSelectedTopics(prev => 
            prev.includes(topic) 
                ? prev.filter(t => t !== topic)
                : [...prev, topic]
        );
    };

    return (
        <Container maxWidth="lg" sx={{ py: 4, backgroundColor: 'white', minHeight: '100vh' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
                <Button
                    startIcon={<ArrowBack />}
                    onClick={() => navigate('/profile')}
                >
                    Back
                </Button>
                <Typography variant="h4" fontWeight="600" sx={{ flex: 1, textAlign: 'center' }}>
                    Shorts
                </Typography>
                <Box sx={{ width: 100 }} /> {/* Spacer for centering */}
            </Box>

            {/* Topic Buttons */}
            <Box sx={{ mb: 4 }}>
                <Box
                    sx={{
                        display: 'flex',
                        gap: 1.5,
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                    }}
                >
                    {mockTopics.map((topic) => (
                        <Button
                            key={topic}
                            variant={selectedTopics.includes(topic) ? 'contained' : 'outlined'}
                            onClick={() => handleTopicToggle(topic)}
                            sx={{
                                borderRadius: '20px',
                                px: 3,
                                py: 1,
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '0.95rem',
                                boxShadow: selectedTopics.includes(topic) ? 3 : 0,
                                '&:hover': {
                                    boxShadow: 4,
                                    transform: 'translateY(-2px)',
                                },
                                transition: 'all 0.2s ease',
                            }}
                        >
                            {topic}
                        </Button>
                    ))}
                </Box>
            </Box>

            {loading && !content && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                    <CircularProgress />
                </Box>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {content && (
                <>
                    {content.isPlaceholder ? (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            This section is coming soon! We're working on bringing you amazing shorts content.
                        </Alert>
                    ) : content.items && content.items.length > 0 ? (
                        <>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                                {content.items.map((item) => (
                                    <Box key={item._id} sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc(33.333% - 16px)', lg: 'calc(25% - 22.5px)' } }}>
                                        <VideoPostCard post={item} isShort={true} showTags={false} />
                                    </Box>
                                ))}
                            </Box>

                            {content.totalPages > 1 && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                                    <Pagination
                                        count={content.totalPages}
                                        page={content.currentPage}
                                        onChange={handlePageChange}
                                        color="primary"
                                        size="large"
                                        showFirstButton
                                        showLastButton
                                    />
                                </Box>
                            )}
                        </>
                    ) : (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            No shorts available yet.
                        </Alert>
                    )}
                </>
            )}
        </Container>
    );
};

export default Shorts; 